import { useLenis } from "lenis/react";
import { ArrowUp } from "lucide-react";
import { useContent } from "../../context/ContentContext";
import { SocialIcons } from "./SocialIcons";
import { NewsletterSignup } from "./NewsletterSignup";

export const Footer = () => {
  const { content } = useContent();
  const c = content.contact || {};
  const f = content.footer || {};
  const logo = f.logo || content.brand?.logo || "/karams-logo.png";
  // Logo ORIGINAL (/karams-logo.png) → affiché en blanc dans le footer (filtre CSS, fichier intact).
  // Un logo personnalisé téléversé dans l'admin s'affiche tel quel, sans filtre.
  const isOriginalLogo = !logo || logo === "/karams-logo.png";
  const lenis = useLenis();
  const go = (id) => {
    // On the festival universe (or any non-home route), section anchors don't exist
    // on the current page → navigate back to the main Karams Studio site at that section.
    if (window.location.pathname !== "/") { window.location.href = `/#${id}`; return; }
    const el = document.getElementById(id);
    if (el && lenis) lenis.scrollTo(el, { duration: 1.4 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };
  const backTop = () => {
    if (window.location.pathname !== "/") {
      if (lenis) lenis.scrollTo(0, { duration: 1 }); else window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    go("hero");
  };

  return (
    <footer className="bg-[#050505] border-t border-white/10 pt-14 pb-10" data-testid="site-footer">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="flex flex-col lg:flex-row justify-between gap-12 pb-10 border-b border-white/10">
          <div className="max-w-md">
            {/* Logo original du site, affiché en version BLANCHE dans le pied de page (filtre CSS — le fichier n'est pas modifié). Un logo personnalisé téléversé dans l'admin s'affiche tel quel. */}
            <a href="/" data-testid="footer-logo-link" aria-label="Retour à Karams Studio"><img src={logo} alt={content.brand?.name || "Karams Studio"} data-testid="footer-logo-img" className={`h-16 w-auto mb-6 ${isOriginalLogo ? "brightness-0 invert" : ""}`} /></a>
            <p className="font-serif text-3xl md:text-4xl text-white/70 leading-tight" data-testid="footer-tagline">
              {f.tagline || "Éduquer, créer et"} {f.taglineAccent !== "" && <span className="italic text-electric">{f.taglineAccent || "inspirer par le cinéma."}</span>}
            </p>
            <p className="mt-5 text-xs tracking-[0.15em] uppercase text-secondary2/70" data-testid="footer-subtitle">{f.subtitle || "Association audiovisuelle à but non lucratif"}</p>
          </div>

          <div className="flex gap-16">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-5">Suivez-nous</div>
              <SocialIcons socials={c.socials || []} size={16} />
              <a href={`tel:${c.phone}`} className="block mt-6 text-secondary2 hover:text-electric transition-colors text-sm">{c.phone}</a>
              {c.website && <a href={c.website} target="_blank" rel="noreferrer" data-testid="footer-website" className="block mt-2 text-secondary2 hover:text-electric transition-colors text-sm">{c.website.replace(/^https?:\/\//, "")}</a>}
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-5">Menu</div>
              <ul className="space-y-3">
                {(content.nav?.items || []).filter((n) => n.visible !== false).map((n) => (
                  <li key={n.id}>
                    {n.type === "section" || !n.type
                      ? <button data-testid={`footer-nav-${n.target || n.id}`} onClick={() => go(n.target)} className="text-secondary2 hover:text-electric transition-colors text-sm">{n.label}</button>
                      : <a data-testid={`footer-nav-${n.target || n.id}`} href={n.type === "page" ? `/p/${n.target}` : n.target} target={n.type === "url" ? "_blank" : undefined} rel={n.type === "url" ? "noreferrer" : undefined} className="text-secondary2 hover:text-electric transition-colors text-sm">{n.label}</a>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="py-8 border-b border-white/10">
          <NewsletterSignup />
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-6 pt-8">
          <p className="text-white/30 text-xs tracking-wide">{(f.copyright || "© {year} Karams Studio. Tous droits réservés.").replace("{year}", new Date().getFullYear())}</p>
          <button data-testid="footer-back-top" onClick={backTop} className="flex items-center gap-2 text-secondary2 hover:text-electric transition-colors text-xs tracking-[0.2em] uppercase">
            Retour en haut <ArrowUp size={14} />
          </button>
        </div>
      </div>
    </footer>
  );
};
